import React, { type FC, type PropsWithChildren, memo } from 'react';
import { type PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import cn from 'clsx';
import { type Theme } from '../../../types/Theme';
import { DH } from '../../../utils/DataHook';
import { type DeviceType } from '../../../types/DeviceType';
import s from './styles.scss';

export const Container: FC<
  PropsWithChildren<{
    isRTL: boolean;
    deviceType: DeviceType;
    isCollapsed: boolean;
    theme: Theme;
    providerId: PaymentProvider;
    customInput: boolean;
  }>
> = memo((props) => {
  /**
   *  duplicated nodes with the same attributes allow us to use SCSS mixins in a more flexible manner,
   *  e.g. both @match-mobile { @match-rtl {} } and @match-rtl { @match-mobile {} } are valid
   */
  const dataAttr = {
    'data-theme': props.theme,
    'data-device': props.deviceType,
    dir: props.isRTL ? 'rtl' : 'ltr',
  };
  return (
    <div {...dataAttr}>
      <div {...dataAttr}>
        <div
          data-hook={DH.Container}
          className={s.container}
          data-is-collapsed={props.isCollapsed}
          data-provider-id={props.providerId}
          data-custom-input={props.customInput}
          {...dataAttr}
        >
          {props.children}
        </div>
      </div>
    </div>
  );
});
Container.displayName = 'Container';

export const Form: FC<
  PropsWithChildren<{
    isHidden: boolean;
    isFullWidth?: boolean;
    omitOffset?: boolean;
  }>
> = memo((props) => (
  <div
    data-hook={DH.Form}
    data-is-hidden={props.isHidden}
    className={cn(s.form, { [s.formFullWidth]: props.isFullWidth, [s.formOmitOffset]: props.omitOffset })}
  >
    {props.children}
  </div>
));
Form.displayName = 'Form';

export const Fields: FC<PropsWithChildren<{}>> = memo((props) => (
  <div className={s.fieldsWrapper}>{props.children}</div>
));
Fields.displayName = 'Fields';

export const Line: FC<PropsWithChildren<{}>> = memo((props) => (
  <div className={s.line} data-hook={DH.FieldsLine}>
    {props.children}
  </div>
));
Line.displayName = 'Line';

export const FieldsGroup: FC<PropsWithChildren<{}>> = memo((props) => (
  <div className={s.responsiveFieldGroup}>{props.children}</div>
));
FieldsGroup.displayName = 'FieldsGroup';
